/* Basis — native section behaviour.
 *
 * Progressive enhancement only. Every section this touches is fully laid out
 * and readable before this file runs; nothing here is required for display.
 *
 * Carousels are CSS scroll-snap tracks (overflow-x:auto). Touch swipe and
 * trackpad scroll are already native. What browsers do NOT give you is
 * click-and-drag with a mouse, so desktop visitors could only advance with
 * the arrows or thumbnails. That is what this adds.
 */
(function () {
  'use strict';
  // Loaded once from <head>, but guard anyway: a second execution would bind a
  // second set of arrow listeners and advance the carousel N slides per click.
  if (window.__bnInit) return;
  window.__bnInit = true;

  var SNAP = '[data-bn-carousel]';

  /* ---------------------------------------------------------------- *
   * Drag to scroll (mouse / pen only — touch is left to the browser,
   * which already does it better and with proper momentum).
   * ---------------------------------------------------------------- */
  function enableDrag(track) {
    if (track.__bnDrag) return;
    track.__bnDrag = true;

    var down = false, moved = false, startX = 0, startLeft = 0, pid = null;

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;        // native handles touch
      if (e.button !== 0) return;                   // primary button only
      down = true; moved = false; pid = e.pointerId;
      startX = e.clientX;
      startLeft = track.scrollLeft;
      track.classList.add('is-grabbing');
      // Snap fights per-pixel dragging; restore it on release.
      track.style.scrollSnapType = 'none';
    });

    track.addEventListener('pointermove', function (e) {
      if (!down || e.pointerId !== pid) return;
      var dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 4) {
        moved = true;
        // Only capture once we know it is a drag, so plain clicks still work.
        try { track.setPointerCapture(pid); } catch (err) {}
      }
      if (!moved) return;
      e.preventDefault();
      track.scrollLeft = startLeft - dx;
    });

    function release(e) {
      if (!down || (e && e.pointerId !== pid)) return;
      down = false;
      track.classList.remove('is-grabbing');
      track.style.scrollSnapType = '';
      if (pid !== null) { try { track.releasePointerCapture(pid); } catch (err) {} }
      pid = null;
      if (moved) snapNearest(track);
    }

    track.addEventListener('pointerup', release);
    track.addEventListener('pointercancel', release);
    track.addEventListener('pointerleave', release);

    // Suppress the click that follows a drag so a dragged slide does not
    // also activate a link inside it.
    track.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);

    track.addEventListener('dragstart', function (e) { e.preventDefault(); });
  }

  function slidesOf(track) {
    return [].slice.call(track.children);
  }

  function activeIndex(track) {
    var slides = slidesOf(track), best = 0, bestD = Infinity;
    for (var i = 0; i < slides.length; i++) {
      var d = Math.abs(slides[i].offsetLeft - track.offsetLeft - track.scrollLeft);
      if (d < bestD) { bestD = d; best = i; }
    }
    return best;
  }

  function goTo(track, i) {
    var slides = slidesOf(track);
    if (!slides.length) return;
    i = Math.max(0, Math.min(slides.length - 1, i));
    track.scrollTo({ left: slides[i].offsetLeft - track.offsetLeft, behavior: 'smooth' });
  }

  function snapNearest(track) { goTo(track, activeIndex(track)); }

  /* ---------------------------------------------------------------- *
   * Arrows + thumbnail sync
   * ---------------------------------------------------------------- */
  function wire(root) {
    var track = root.matches && root.matches(SNAP) ? root : root.querySelector(SNAP);
    if (!track) return;
    enableDrag(track);

    var scope = track.closest('[data-bn-carousel-scope]') || track.parentElement;

    var prev = scope && scope.querySelector('[data-bn-prev]');
    var next = scope && scope.querySelector('[data-bn-next]');
    if (prev) prev.addEventListener('click', function (e) { e.preventDefault(); goTo(track, activeIndex(track) - 1); });
    if (next) next.addEventListener('click', function (e) { e.preventDefault(); goTo(track, activeIndex(track) + 1); });

    var thumbs = scope ? [].slice.call(scope.querySelectorAll('[data-bn-thumb]')) : [];
    thumbs.forEach(function (t) {
      t.addEventListener('click', function (e) { e.preventDefault(); goTo(track, +t.getAttribute('data-bn-thumb')); });
    });

    // Keyboard support for the track itself.
    track.setAttribute('tabindex', track.getAttribute('tabindex') || '0');
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(track, activeIndex(track) + 1); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); goTo(track, activeIndex(track) - 1); }
    });

    var sync = function () {
      var i = activeIndex(track);
      thumbs.forEach(function (t, n) { t.classList.toggle('is-active', n === i); });
      if (prev) prev.disabled = i <= 0;
      if (next) next.disabled = i >= slidesOf(track).length - 1;
    };
    track.addEventListener('scroll', sync, { passive: true });
    sync();
  }


  /* ---------------------------------------------------------------- *
   * YouTube facades. Tiles are real links until clicked; on click we
   * swap in the iframe so a wall of ten testimonials costs ten
   * thumbnails at load instead of ten embedded players.
   * ---------------------------------------------------------------- */
  function wireYouTube() {
    document.addEventListener('click', function (e) {
      var tile = e.target.closest && e.target.closest('[data-bn-youtube]');
      if (!tile) return;
      var id = tile.getAttribute('data-bn-youtube');
      if (!id || tile.__bnPlayed) return;
      e.preventDefault();
      tile.__bnPlayed = true;
      var f = document.createElement('iframe');
      f.src = 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&rel=0';
      f.title = tile.getAttribute('aria-label') || 'Video';
      f.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      f.setAttribute('allowfullscreen', '');
      f.className = 'bn-vw__iframe';
      tile.innerHTML = '';
      tile.appendChild(f);
      tile.classList.add('is-playing');
    });
  }

  function init() {
    [].forEach.call(document.querySelectorAll(SNAP), wire);
    wireYouTube();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
